/**
 * 应用入口：把「取包 → 引擎加载 → 渲染 → 交互 → 任务判定 → 反馈」串起来。
 *
 * 【bug/023-化学/001-bug.md 的三条都修在这个文件里】
 * 之前 M1b 做了 Mission / 规则 / 安全 / 知识点四层，但 `app.ts` **从来没调用过
 * `engine.settle()`** —— 整个逻辑层对用户是不存在的。表现就是用户说的：
 *   ① 连没连上看不出来（只有日志滚一行字）
 *   ② 连不上不知道为什么（只有一句「没有找到可连接的位置」）
 *   ③ 知识点和错误提示压根没出现
 *
 * 这个文件是**产物侧的挂载壳**：按 v1.4 §12.3.1，054 拆分工程边界后它会变成
 * `source/chrome-chemistry/src/main.ts`，所以这里允许出现实验包名等产物级常量。
 */

import "./styles.css";
import { listPackDirs, loadExperimentPack } from "./data/pack-loader.js";
import { LAB_MODES, type LabMode } from "./data/schemas.js";
import { LabEngine, ENGINE_EVENTS } from "./core/lab-engine.js";
import { ANY_EVENT } from "./core/event-bus.js";
import { SvgLayer } from "./animation/svg-layer.js";
import { listObjects } from "./eom/assembly-graph.js";
import { PointerController } from "./interaction/pointer-controller.js";
import type { SnapFailure } from "./interaction/snap-controller.js";
import type { KnowledgeSource } from "./knowledge/knowledge-engine.js";

const DEFAULT_PACK_DIR = "oxygen-kmno4";
const MAX_LOG_ROWS = 200;
/** 过程要靠时间推进（加热、集气），所以要有心跳，不能只在改图时 settle。 */
const TICK_MS = 200;

function requireElement<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`页面缺少必需节点 #${id}`);
  return node as T;
}

function isLabMode(value: string): value is LabMode {
  return (LAB_MODES as readonly string[]).includes(value);
}

const ui = {
  stage: requireElement<HTMLElement>("lab-stage"),
  packTitle: requireElement<HTMLParagraphElement>("lab-pack-title"),
  packSelect: requireElement<HTMLSelectElement>("lab-pack-select"),
  modeSelect: requireElement<HTMLSelectElement>("lab-mode"),
  resetButton: requireElement<HTMLButtonElement>("lab-reset"),
  status: requireElement<HTMLElement>("lab-status"),
  log: requireElement<HTMLOListElement>("lab-log"),
  stepCounter: requireElement<HTMLElement>("lab-step-counter"),
  stepProgress: requireElement<HTMLElement>("lab-step-progress"),
  stepTitle: requireElement<HTMLElement>("lab-step-title"),
  stepGoal: requireElement<HTMLElement>("lab-step-goal"),
  stepDetail: requireElement<HTMLElement>("lab-step-detail"),
  stepList: requireElement<HTMLOListElement>("lab-steps"),
  feedback: requireElement<HTMLElement>("lab-feedback"),
  hint: requireElement<HTMLElement>("lab-hint"),
  knowledge: requireElement<HTMLElement>("lab-knowledge"),
  knowledgeBody: requireElement<HTMLElement>("lab-knowledge-body"),
};

const engine = new LabEngine();
const svgLayer = new SvgLayer();
let ticker: ReturnType<typeof setInterval> | null = null;
let startedAt = 0;

function nowMs(): number {
  return performance.now() - startedAt;
}

function nameOf(objectId: string | null | undefined): string {
  if (!objectId) return "器材";
  return engine.getState().graph?.nodes[objectId]?.name ?? objectId;
}

/* ── 反馈条（bug 第 1、2 条） ─────────────────────────────── */

type Tone = "idle" | "success" | "warn" | "danger" | "info";

function setFeedback(text: string, tone: Tone = "info"): void {
  ui.hint.textContent = text;
  ui.feedback.dataset.tone = tone;
}

/**
 * 把失败原因翻译成学生能照着改的一句话。
 *
 * bug 第 2 条原话：「不知道是代码问题，标注问题，还是什么，总是说连不上」。
 * 四种原因下一步动作完全不同，所以必须分开说，且都要给出**下一步该做什么**。
 */
function describeFailure(failure: SnapFailure | null): string {
  if (!failure) {
    return "这里附近没有可以连接的东西。把器材拖到别的器材的连接点上试试。";
  }

  const target = nameOf(failure.targetObjectId);
  switch (failure.reason) {
    case "too_far": {
      const gap = Math.max(0, Math.round((failure.distance ?? 0) - (failure.radius ?? 0)));
      return `方向对了，就是差一点：「${failure.sourceAnchorLabel}」离${target}的「${failure.targetAnchorLabel}」还有约 ${gap}px，再靠近一些松手。`;
    }
    case "occupied":
      return `${target}的「${failure.targetAnchorLabel}」已经连着别的器材了。先把那个拖走，再接这个。`;
    case "no_action":
      return `「${failure.sourceAnchorLabel}」和${target}的「${failure.targetAnchorLabel}」凑不成一个实验动作。换个连接点试试。`;
    case "incompatible":
      return `这两件器材接不到一起 —— 它们在实验里本来就不该相连。看看左侧步骤要求接哪两样。`;
  }
}

/* ── 步骤面板（bug 第 3 条） ──────────────────────────────── */

function renderStepList(): void {
  const mission = engine.getMission();
  if (!mission) return;
  const state = mission.getState();

  ui.stepList.replaceChildren();
  mission.getSteps().forEach((step, index) => {
    const row = document.createElement("li");
    const record = state.records[index];
    const done = record?.status === "passed";
    row.className = "lab-step-row";
    row.dataset.state = done ? "done" : index === state.currentIndex ? "current" : "todo";

    const mark = document.createElement("span");
    mark.className = "lab-step-mark";
    mark.textContent = done ? "✓" : String(index + 1);

    const text = document.createElement("span");
    text.textContent = step.title;

    row.append(mark, text);
    if ((record?.errorCount ?? 0) > 0) {
      const badge = document.createElement("span");
      badge.className = "lab-step-badge";
      badge.textContent = `错 ${record!.errorCount}`;
      row.append(badge);
    }
    ui.stepList.append(row);
  });
}

function renderRuntime(): void {
  const mission = engine.getMission();
  if (!mission) return;

  const snapshot = engine.settle(nowMs());
  const state = mission.getState();

  if (snapshot.completed) {
    ui.stepCounter.textContent = `全部 ${snapshot.stepCount} 步完成`;
    ui.stepProgress.textContent = "🎉";
    ui.stepTitle.textContent = "实验完成";
    ui.stepGoal.textContent = "你已经走完高锰酸钾制取氧气的全流程。";
    ui.stepDetail.textContent = "";
  } else {
    ui.stepCounter.textContent = `第 ${state.currentIndex + 1} / ${snapshot.stepCount} 步`;
    ui.stepProgress.textContent =
      snapshot.progress.total > 0 ? `${snapshot.progress.met}/${snapshot.progress.total}` : "";
    ui.stepTitle.textContent = snapshot.hint?.title ?? "";
    ui.stepGoal.textContent = snapshot.hint?.goal ?? "";
    ui.stepDetail.textContent = snapshot.hint?.detail ?? "";
  }

  renderStepList();

  // §7.1 modeDefaults.showGhost 的落地：告诉渲染层这一步该动哪几件器材。
  // 实验包早就声明了 showGhost: true，之前引擎零消费，学生因此不知道该拖什么。
  const mode = engine.getState().mode;
  svgLayer.setStepTargets(
    snapshot.completed ? [] : mission.currentStepObjectIds(),
    mission.showGhostFor(mode),
  );
  svgLayer.scheduleSync(engine.getGraph(), mode);
}

/* ── 知识点卡片（bug 第 3 条） ────────────────────────────── */

function showKnowledge(id: string, source: KnowledgeSource): void {
  const show = engine.getKnowledge().trigger(id, source);
  if (!show) return;

  const card = document.createElement("article");
  card.className = "lab-knowledge-card";
  card.dataset.level = show.item.level ?? "normal";
  card.dataset.source = source;

  const title = document.createElement("h3");
  title.textContent = show.item.title;
  const body = document.createElement("p");
  body.textContent = show.item.content;

  card.append(title, body);
  ui.knowledgeBody.replaceChildren(card);
  ui.knowledge.hidden = false;
}

/* ── 日志 ─────────────────────────────────────────────────── */

function appendLog(seq: number, type: string, detail: string): void {
  const row = document.createElement("li");
  const seqCell = document.createElement("span");
  seqCell.className = "seq";
  seqCell.textContent = seq > 0 ? String(seq) : "·";
  const typeCell = document.createElement("span");
  typeCell.className = "type";
  typeCell.textContent = type;
  const detailCell = document.createElement("span");
  detailCell.textContent = detail;

  row.append(seqCell, typeCell, detailCell);
  ui.log.append(row);
  while (ui.log.childElementCount > MAX_LOG_ROWS) ui.log.firstElementChild?.remove();
  ui.log.scrollTop = ui.log.scrollHeight;
}

/* ── 指针交互 ─────────────────────────────────────────────── */

const pointer = new PointerController({
  getGraph: () => engine.getGraph(),
  setGraph: (graph) => {
    engine.setGraph(graph);
    svgLayer.scheduleSync(graph, engine.getState().mode);
  },
  getDesiredRelationKeys: () => engine.desiredRelationKeys(),

  onDragStart: (objectId) => {
    setFeedback(`正在拖动「${nameOf(objectId)}」，靠近可连接的位置会出现虚线。`, "info");
  },

  onDragMove: (result) => {
    const bond = result.candidate?.bond;
    if (bond) {
      setFeedback(
        `松手即可连接：${nameOf(bond.from.objectId)} → ${nameOf(bond.to.objectId)}`,
        "success",
      );
    }
    for (const relation of result.broken) {
      appendLog(0, "断开", `${nameOf(relation.from.objectId)} ✕ ${nameOf(relation.to.objectId)}`);
      setFeedback(`已断开：${nameOf(relation.from.objectId)} 与 ${nameOf(relation.to.objectId)}`, "warn");
    }
  },

  onDragEnd: (result) => {
    const relation = result.connected?.relation;
    if (relation && !result.connected?.duplicated) {
      appendLog(0, "连接成功", `${nameOf(relation.from.objectId)} → ${nameOf(relation.to.objectId)}`);
      setFeedback(`✓ 连接成功：${nameOf(relation.from.objectId)} → ${nameOf(relation.to.objectId)}`, "success");
      renderRuntime();
      svgLayer.pulseJoint(relation.id);
      return;
    }
    if (!result.wasClick) {
      setFeedback(describeFailure(result.failure), "warn");
    }
    renderRuntime();
  },

  onTap: (objectId) => {
    // 点击可点燃器材 = 切换点燃状态（补 report/001 坑 B 的「熄灭」入口）
    const object = engine.getGraph().nodes[objectId];
    if (!object?.capabilities.includes("ignitable")) return;
    const lit = object.state.lit === true;
    engine.setGraph({
      ...engine.getGraph(),
      nodes: {
        ...engine.getGraph().nodes,
        [objectId]: { ...object, state: { ...object.state, lit: !lit } },
      },
    });
    setFeedback(lit ? `已熄灭${object.name}。` : `已点燃${object.name}。`, lit ? "info" : "success");
    renderRuntime();
  },

  onLongPress: (objectId) => {
    setFeedback(`「${nameOf(objectId)}」的连接点已高亮，把它们拖到目标上。`, "info");
  },
});

/* ── 生命周期 ─────────────────────────────────────────────── */

function renderCurrentState(): void {
  const { scene, graph, mode } = engine.getState();
  if (!scene || !graph) return;
  svgLayer.render(scene, listObjects(graph), mode);
  const root = svgLayer.getRoot();
  if (root) pointer.attach(root);
  svgLayer.sync(graph, mode);
}

async function openPack(packDir: string): Promise<void> {
  setStatus(`加载实验包 ${packDir} …`);
  const pack = await loadExperimentPack(packDir);
  await engine.load(pack);

  const mode = isLabMode(ui.modeSelect.value) ? ui.modeSelect.value : "learn";
  startedAt = performance.now();
  engine.start(mode, 0);
  renderCurrentState();
  renderRuntime();

  const objectCount = Object.keys(engine.getState().graph?.nodes ?? {}).length;
  ui.packTitle.textContent = `${pack.title} · ${pack.experimentType} · ${pack.difficulty}`;
  setStatus(`已加载 ${pack.id}：${objectCount} 件器材，模式 ${mode}。`);
  setFeedback("按左侧步骤操作。把器材拖到一起，靠近连接点会出现虚线提示。", "idle");

  if (ticker !== null) clearInterval(ticker);
  ticker = setInterval(renderRuntime, TICK_MS);
}

function setStatus(text: string, level: "info" | "error" = "info"): void {
  ui.status.textContent = text;
  ui.status.dataset.level = level;
}

function bindControls(): void {
  ui.packSelect.addEventListener("change", () => {
    void openPack(ui.packSelect.value).catch(reportFailure);
  });

  ui.modeSelect.addEventListener("change", () => {
    if (!isLabMode(ui.modeSelect.value)) return;
    engine.setMode(ui.modeSelect.value);
    renderCurrentState();
    renderRuntime();
  });

  ui.resetButton.addEventListener("click", () => {
    engine.reset();
    startedAt = performance.now();
    renderCurrentState();
    ui.knowledge.hidden = true;
    ui.log.replaceChildren();
    renderRuntime();
    setStatus("已重置到实验包初始状态。");
    setFeedback("已重置。从第一步重新开始。", "idle");
  });
}

function reportFailure(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  setStatus(message, "error");
  console.error("[LabEngine]", error);
}

function bindEngineEvents(): void {
  engine.bus.on(ANY_EVENT, (event) => {
    // 高频的内部事件不进日志，否则学生看到的全是噪声
    if (event.type === ENGINE_EVENTS.knowledge || event.type === ENGINE_EVENTS.animate) return;
    appendLog(event.seq, event.type, JSON.stringify(event.payload));
  });

  engine.bus.on(ENGINE_EVENTS.knowledge, (event) => {
    const payload = event.payload as { id: string; source: KnowledgeSource };
    showKnowledge(payload.id, payload.source);
  });

  engine.bus.on(ENGINE_EVENTS.safety, (event) => {
    const payload = event.payload as { code: string; severity: "info" | "warn" | "danger" };
    setFeedback(
      payload.severity === "danger" ? "⚠ 危险操作！看右侧知识点。" : "注意：这一步操作有问题。",
      payload.severity === "danger" ? "danger" : "warn",
    );
  });

  engine.bus.on(ENGINE_EVENTS.stepPassed, () => {
    setFeedback("✓ 这一步完成了，看左侧下一步。", "success");
  });

  engine.bus.on(ENGINE_EVENTS.stepBlocked, () => {
    setFeedback("这一步还不能做 —— 前面有步骤没完成。", "warn");
  });

  engine.bus.on(ENGINE_EVENTS.missionCompleted, () => {
    setFeedback("🎉 实验全部完成！", "success");
  });
}

function bootstrap(): void {
  bindEngineEvents();

  const packDirs = listPackDirs();
  for (const dir of packDirs) {
    const option = document.createElement("option");
    option.value = dir;
    option.textContent = dir;
    ui.packSelect.append(option);
  }

  if (packDirs.length === 0) {
    setStatus("experiments/ 下没有实验包。", "error");
    return;
  }

  const initial = packDirs.includes(DEFAULT_PACK_DIR) ? DEFAULT_PACK_DIR : packDirs[0]!;
  ui.packSelect.value = initial;
  svgLayer.mount(ui.stage);
  bindControls();
  void openPack(initial).catch(reportFailure);
}

bootstrap();
